import { Activity, Heart, CheckCircle2, Edit2, Trash2, Eye, Bug, Code } from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { getServiceType } from '@/constants/service-types'
import { getCategory } from '@/constants/service-categories'
import { ServiceLogo } from '@/components/ServiceLogo'

interface ServiceCardProps {
  service: AIService
  onEdit: (service: AIService, e: React.MouseEvent) => void
  onDelete: (id: string, e: React.MouseEvent) => void
  onViewDetail: (service: AIService) => void
  onDebug: (service: AIService) => void
  onCodeTemplate: (service: AIService) => void
}

export function ServiceCard({ service, onEdit, onDelete, onViewDetail, onDebug, onCodeTemplate }: ServiceCardProps) {
  const serviceTypeInfo = getServiceType(service.provider)
  const categoryInfo = service.category ? getCategory(service.category) : null

  return (
    <div
      className={`group relative rounded-2xl border-l-4 border-r border-t border-b border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 overflow-hidden ${serviceTypeInfo?.gradient || 'bg-gradient-to-br from-white to-gray-50'}`}
      style={categoryInfo ? { borderLeftColor: categoryInfo.color.accent } : undefined}
    >
      {/* Decorative Logo - 右下角半透明显示服务提供商 logo */}
      <div className="absolute bottom-4 right-4 pointer-events-none">
        <ServiceLogo
          provider={service.provider}
          name={service.name}
          decorative
          size="2xl"
        />
      </div>

      {/* Status Badge - 右上角 */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {/* Status */}
        {service.status === 'active' ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-full">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-green-700">运行中</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-xs font-medium text-gray-600">已停用</span>
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Icon and Title */}
        <div className="flex items-start gap-4 mb-4">
          {/* Service Custom Icon */}
          <div className="w-14 h-14 rounded-xl bg-white shadow-lg flex items-center justify-center overflow-hidden flex-shrink-0">
            {service.icon_url ? (
              <img
                src={service.icon_url}
                alt={service.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // 如果自定义图标加载失败，显示默认图标
                  e.currentTarget.src = 'https://flowspark.net/favicon.ico'
                }}
              />
            ) : (
              <img
                src="https://flowspark.net/favicon.ico"
                alt="Default icon"
                className="w-8 h-8 object-contain"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">{service.name}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {categoryInfo && (
                <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${categoryInfo.color.badge}`}>
                  {categoryInfo.icon && <categoryInfo.icon className="w-3 h-3" />}
                  {categoryInfo.name}
                </span>
              )}
              {serviceTypeInfo && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${serviceTypeInfo.bgColor} ${serviceTypeInfo.color} font-medium`}>
                  {serviceTypeInfo.displayName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
          {service.description || '暂无描述'}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 border border-gray-100">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <Activity className="w-3 h-3" />
              <span>总调用</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {(service.usage_count || 0)}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-green-100">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <CheckCircle2 className="w-3 h-3" />
              <span>成功率</span>
            </div>
            <div className="text-lg font-bold text-green-600">
              {service.usage_count && service.usage_count > 0
                ? `${Math.round(((service.success_count || 0) / service.usage_count) * 100)}%`
                : '0%'
              }
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-100">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <Heart className="w-3 h-3" />
              <span>积分</span>
            </div>
            <div className="text-lg font-bold text-blue-600">
              {service.points_cost}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t border-gray-200/50">
          {/* 主要操作 */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onViewDetail(service)
            }}
            className="flex-1 flex items-center justify-center p-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition shadow-sm group"
            title="查看详情"
          >
            <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDebug(service)
            }}
            className="flex-1 flex items-center justify-center p-3 text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:from-purple-700 hover:to-pink-700 transition shadow-sm group"
            title="调试测试"
          >
            <Bug className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCodeTemplate(service)
            }}
            className="flex-1 flex items-center justify-center p-3 text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:from-emerald-700 hover:to-teal-700 transition shadow-sm group"
            title="代码模板"
          >
            <Code className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={(e) => onEdit(service, e)}
            className="flex-1 flex items-center justify-center p-3 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition group"
            title="编辑"
          >
            <Edit2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={(e) => onDelete(service.id, e)}
            className="flex-1 flex items-center justify-center p-3 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition group"
            title="删除"
          >
            <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  )
}
